import { useEffect, useState } from "react";

/* UI-DESIGN: LB-14 which version of the Home a visitor gets.
   - "three": the scroll-driven lit scene (desktop, WebGL, motion allowed)
   - "static": a clean stack of the same sections, CSS light, no scene
     (reduced motion, or no WebGL)
   - "frames": phones (768 px and under) get pre-rendered frames of the
     same scene instead of live WebGL (LB-16)
   `?story=three|static|frames` forces one, for review. */
export type StoryMode = "three" | "static" | "frames";

const hasWebGL = () => {
  try {
    const c = document.createElement("canvas");
    /* UI-DESIGN: this Three.js renderer needs WebGL 2. Release the probe
       context rather than keeping an extra GPU context for the page. */
    const gl = c.getContext("webgl2");
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
    return Boolean(gl);
  } catch {
    return false;
  }
};

const pick = (): StoryMode => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return "static";
  const forced = new URLSearchParams(window.location.search).get("story");
  if (forced === "three" || forced === "static" || forced === "frames") return forced;
  if (window.matchMedia("(max-width: 768px)").matches) return "frames";
  return hasWebGL() ? "three" : "static";
};

export const useStoryMode = (): StoryMode => {
  const [mode, setMode] = useState<StoryMode>(pick);
  useEffect(() => {
    const queries = [
      window.matchMedia("(max-width: 768px)"),
      window.matchMedia("(prefers-reduced-motion: reduce)"),
    ];
    const update = () => setMode(pick());
    queries.forEach((q) => q.addEventListener("change", update));
    return () => queries.forEach((q) => q.removeEventListener("change", update));
  }, []);
  return mode;
};
